import { useState } from 'react';
import { CheckCircle2, Globe, Loader2, Mail, Send } from 'lucide-react';
import { WhatsAppIcon } from '../components/WhatsAppIcon';
import { useLanguage } from '../i18n';
import { Reveal } from '../components/Reveal';
import { Overline } from '../components/Overline';
import { PageHero } from '../components/PageHero';
import { WHATSAPP_URL } from '../lib/whatsapp';

const inputClass =
  'w-full rounded-sm bg-graphite-200 px-4 py-3 text-sm text-steel-bright placeholder:text-steel-label transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-silver';

const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', whatsapp: '', company: '', message: '', company_alt: '' });
  const [status, setStatus] = useState('idle');

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'loading') return;
    setStatus('loading');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('send failed');
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div data-testid="contact-page" className="pt-16">
      <section>
        <div className="mx-auto max-w-7xl px-6 py-24">
          <PageHero overline={t.contact.overline} title={t.contact.title} intro={t.contact.intro} titleClassName="" />

          <div className="mt-16 grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              {status === 'success' ? (
                <div
                  className="flex flex-col items-start gap-4 bg-graphite-100 p-10"
                  data-testid="contact-form-success"
                >
                  <CheckCircle2 size={32} className="text-silver" />
                  <h2 className="text-2xl font-semibold tracking-tight text-steel-bright">
                    {t.contact.form.successTitle}
                  </h2>
                  <p className="text-sm leading-relaxed text-steel">{t.contact.form.successMsg}</p>
                </div>
              ) : (
                <Reveal delay={0.1}>
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="contact-name" className="mb-2 block font-mono text-xs uppercase tracking-[0.15em] text-steel-label">
                          {t.contact.form.name} *
                        </label>
                        <input
                          id="contact-name"
                          data-testid="contact-name-input"
                          required
                          value={form.name}
                          onChange={set('name')}
                          placeholder={t.contact.form.namePh}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="mb-2 block font-mono text-xs uppercase tracking-[0.15em] text-steel-label">
                          {t.contact.form.email} *
                        </label>
                        <input
                          id="contact-email"
                          type="email"
                          data-testid="contact-email-input"
                          required
                          value={form.email}
                          onChange={set('email')}
                          placeholder={t.contact.form.emailPh}
                          className={inputClass}
                        />
                      </div>
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="contact-whatsapp" className="mb-2 block font-mono text-xs uppercase tracking-[0.15em] text-steel-label">
                          {t.contact.form.whatsapp} *
                        </label>
                        <input
                          id="contact-whatsapp"
                          data-testid="contact-whatsapp-input"
                          required
                          minLength={8}
                          value={form.whatsapp}
                          onChange={set('whatsapp')}
                          placeholder={t.contact.form.whatsappPh}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-company" className="mb-2 block font-mono text-xs uppercase tracking-[0.15em] text-steel-label">
                          {t.contact.form.company}
                        </label>
                        <input
                          id="contact-company"
                          data-testid="contact-company-input"
                          value={form.company}
                          onChange={set('company')}
                          placeholder={t.contact.form.companyPh}
                          className={inputClass}
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="mb-2 block font-mono text-xs uppercase tracking-[0.15em] text-steel-label">
                        {t.contact.form.message} *
                      </label>
                      <textarea
                        id="contact-message"
                        data-testid="contact-message-input"
                        required
                        minLength={10}
                        rows={5}
                        value={form.message}
                        onChange={set('message')}
                        placeholder={t.contact.form.messagePh}
                        className={`${inputClass} resize-y`}
                      />
                    </div>

                    <div className="hp-field" aria-hidden="true">
                      <label htmlFor="company-alt">Company</label>
                      <input
                        id="company-alt"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        data-testid="contact-honeypot-input"
                        value={form.company_alt}
                        onChange={set('company_alt')}
                      />
                    </div>

                    {status === 'error' && (
                      <p className="rounded-sm bg-red-950/60 px-4 py-3 text-sm text-red-300" data-testid="contact-form-error">
                        {t.contact.form.errorMsg}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      data-testid="contact-submit-button"
                      className="group inline-flex w-full items-center justify-center gap-3 rounded-sm bg-silver px-7 py-3.5 text-sm font-medium text-graphite transition-colors duration-200 hover:bg-silver-glow disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                    >
                      {status === 'loading' ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          {t.contact.form.sending}
                        </>
                      ) : (
                        <>
                          {t.contact.form.submit}
                          <Send size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                        </>
                      )}
                    </button>
                  </form>
                </Reveal>
              )}
            </div>

            <div className="lg:col-span-4 lg:col-start-9">
              <Reveal delay={0.2}>
                <div
                  className="lg:border-l lg:border-graphite-200 lg:pl-10"
                  data-testid="contact-channels-card"
                >
                  <Overline>{t.contact.channelsTitle}</Overline>
                  <ul className="mt-8 space-y-8">
                    <li>
                      <p className="font-mono text-xs uppercase tracking-[0.15em] text-steel-label">
                        {t.contact.whatsappLabel}
                      </p>
                      <a
                        href={WHATSAPP_URL(t.whatsappMessage)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-whatsapp-link"
                        className="mt-2 flex items-center gap-3 text-lg font-medium text-silver transition-colors duration-200 hover:text-silver-glow"
                      >
                        <WhatsAppIcon size={20} className="text-steel-label" />
                        {t.contact.whatsappValue}
                      </a>
                    </li>
                    <li>
                      <p className="font-mono text-xs uppercase tracking-[0.15em] text-steel-label">
                        {t.contact.emailLabel}
                      </p>
                      <a
                        href={`mailto:${t.contact.emailValue}`}
                        data-testid="contact-email-link"
                        className="mt-2 flex items-center gap-3 text-lg font-medium text-silver transition-colors duration-200 hover:text-silver-glow"
                      >
                        <Mail size={20} className="text-steel-label" />
                        {t.contact.emailValue}
                      </a>
                    </li>
                    <li>
                      <p className="font-mono text-xs uppercase tracking-[0.15em] text-steel-label">
                        {t.contact.locationLabel}
                      </p>
                      <p className="mt-2 flex items-center gap-3 text-lg font-medium text-steel-bright">
                        <Globe size={20} className="shrink-0 text-steel-label" />
                        {t.contact.locationValue}
                      </p>
                    </li>
                  </ul>
                  <p className="mt-10 border-t border-graphite-200 pt-6 font-mono text-xs text-steel-label">
                    {t.contact.soon}
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
